import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Shirt } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

interface UserHoverCardProps {
  userId: string;
  displayName?: string;
  avatarUrl?: string;
  children: React.ReactNode;
}

interface ProfileData {
  display_name: string | null;
  username: string | null;
  avatar_url: string | null;
  bio: string | null;
}

export default function UserHoverCard({ userId, displayName, avatarUrl, children }: UserHoverCardProps) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [kitsCount, setKitsCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [openTimer, setOpenTimer] = useState<ReturnType<typeof setTimeout> | null>(null);
  const canHover = typeof window !== "undefined" && window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  const OPEN_DELAY_MS = 350;

  const loadProfile = async () => {
    if (loaded) return;
    const [profileRes, kitsRes] = await Promise.all([
      supabase.from("profiles").select("display_name, username, avatar_url, bio").eq("user_id", userId).single(),
      supabase.from("kits").select("id", { count: "exact", head: true }).eq("user_id", userId).eq("is_public", true),
    ]);
    setProfile(profileRes.data);
    setKitsCount(kitsRes.count || 0);
    setLoaded(true);
  };

  const handleEnter = (e?: React.PointerEvent) => {
    if (!canHover) return;
    // Ignore synthetic hover events from touch/pen taps — they fire after
    // tapping buttons (e.g. "Załaduj więcej") and cause the card to open
    // unexpectedly on whatever element is under the finger after reflow.
    if (e && e.pointerType !== "mouse") return;
    if (openTimer) clearTimeout(openTimer);
    // Delay opening so passing the cursor over avatars during navigation
    // doesn't pop a card right where the user is about to click.
    const id = setTimeout(() => {
      setOpen(true);
      loadProfile();
    }, OPEN_DELAY_MS);
    setOpenTimer(id);
  };

  const handleLeave = (e?: React.PointerEvent) => {
    if (!canHover) return;
    if (e && e.pointerType !== "mouse") return;
    if (openTimer) { clearTimeout(openTimer); setOpenTimer(null); }
    setOpen(false);
  };

  useEffect(() => {
    return () => { if (openTimer) clearTimeout(openTimer); };
  }, [openTimer]);

  return (
    <div
      className="relative"
      onPointerEnter={handleEnter}
      onPointerLeave={handleLeave}
      onPointerDown={() => { if (openTimer) { clearTimeout(openTimer); setOpenTimer(null); } }}
    >
      {children}
      {canHover && open && (
        <>
          {/* Mobile backdrop to allow tap-to-close */}
          <div
            className="fixed inset-0 z-40 sm:hidden"
            onClick={() => setOpen(false)}
          />
        <div
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[calc(100vw-2rem)] max-w-xs sm:absolute sm:left-0 sm:top-full sm:mt-1 sm:translate-x-0 sm:translate-y-0 sm:w-64 sm:max-w-none rounded-xl border bg-card p-4 shadow-lg animate-in fade-in-0 zoom-in-95 duration-150"
          onPointerEnter={handleEnter}
          onPointerLeave={handleLeave}
        >
          <div
            className="flex items-center gap-3 mb-3 cursor-pointer"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/profile/${userId}`); }}
          >
            <Avatar className="h-10 w-10">
              <AvatarImage src={profile?.avatar_url || avatarUrl || undefined} />
              <AvatarFallback className="text-sm">
                {(profile?.username || displayName || "?")[0].toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground hover:underline">
                {profile?.username || "anonim"}
              </p>
            </div>
          </div>

          {profile?.bio && (
            <p className="mb-3 text-xs leading-relaxed text-muted-foreground line-clamp-2">{profile.bio}</p>
          )}

          <div className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground">
            <Shirt className="h-3.5 w-3.5" />
            <span><strong className="text-foreground">{kitsCount}</strong> koszulek</span>
          </div>

          <div className="flex flex-col gap-2">
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-1.5"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/profile/${userId}`);
              }}
            >
              Zobacz profil
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="w-full gap-1.5"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/wiadomosci?with=${userId}`);
              }}
            >
              <Mail className="h-3.5 w-3.5" />
              Napisz wiadomość
            </Button>
          </div>
        </div>
        </>
      )}
    </div>
  );
}
